/** Shared wishlist state. Account synchronization is a separately enabled adapter. */
(function (host, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (host && host.document) api.install(host);
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  var KEY = 'pg:wish';
  var LIMIT = 100;
  var MAX_FRAGMENT = 32768;
  var PREFIX = '#pg-wishlist=v1:';

  function validHandle(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= 255 && /^[\p{L}\p{M}\p{N}_™®-]+$/u.test(value);
  }
  function clean(list) {
    if (!Array.isArray(list)) return [];
    var result = [];
    list.slice(0, 1000).forEach(function (handle) {
      if (result.length < LIMIT && validHandle(handle) && result.indexOf(handle) < 0) result.push(handle);
    });
    return result;
  }
  function parseStored(value) {
    if (typeof value !== 'string' || value.length > 65536) return [];
    try { return clean(JSON.parse(value)); } catch (e) { return []; }
  }
  function validList(list) {
    return Array.isArray(list) && list.length <= LIMIT && list.every(validHandle);
  }
  function encodeShare(list) {
    if (!validList(list) || !list.length) return null;
    var result = PREFIX + clean(list).map(encodeURIComponent).join(',');
    return result.length <= MAX_FRAGMENT ? result : null;
  }
  function decodeShare(fragment) {
    if (typeof fragment !== 'string' || fragment.indexOf('#pg-wishlist=') !== 0) return { list: [], present: false };
    if (fragment.length > MAX_FRAGMENT || fragment.indexOf(PREFIX) !== 0) return { error: 'invalid', present: true };
    var values = fragment.slice(PREFIX.length).split(',');
    if (values.length > LIMIT) return { error: 'invalid', present: true };
    try { values = values.map(decodeURIComponent); } catch (e) { return { error: 'invalid', present: true }; }
    if (!validList(values)) return { error: 'invalid', present: true };
    return { list: clean(values), present: true };
  }

  function createStore(storage, notify) {
    var memory = [];
    var volatile = false;
    var key = KEY;
    var scope = null;
    var locked = false;
    var subscribers = [];
    function read() {
      if (locked) return [];
      if (!volatile) {
        try { memory = parseStored(storage.getItem(key) || '[]'); }
        catch (e) { volatile = true; }
      }
      return memory.slice();
    }
    function emit(error, source, previous) {
      var detail = { list: read(), persisted: !volatile, error: error || (volatile ? 'storage' : null) };
      detail.count = detail.list.length;
      detail.source = source || 'local'; detail.scope = scope; detail.previous = previous || detail.list.slice();
      if (notify) notify(detail);
      subscribers.slice().forEach(function (subscriber) { subscriber(detail); });
      return detail;
    }
    function write(list, options) {
      if (locked) { emit('session', 'session'); return { ok: false, error: 'session' }; }
      if (!validList(list)) return { ok: false, error: Array.isArray(list) && list.length > LIMIT ? 'limit' : 'invalid' };
      var previous = read();
      memory = clean(list);
      try { storage.setItem(key, JSON.stringify(memory)); volatile = false; }
      catch (e) { volatile = true; }
      return Object.assign({ ok: true }, emit(null, options && options.source, previous));
    }
    function toggle(handle) {
      if (!validHandle(handle)) return { ok: false, error: 'invalid' };
      var list = read();
      var index = list.indexOf(handle);
      if (index >= 0) list.splice(index, 1);
      else if (list.length >= LIMIT) { emit('limit'); return { ok: false, error: 'limit' }; }
      else list.push(handle);
      return write(list);
    }
    function importList(list, mode) {
      if (!validList(list) || !list.length || (mode !== 'merge' && mode !== 'replace')) return { ok: false, error: 'invalid' };
      var next = mode === 'replace' ? clean(list) : read();
      if (mode === 'merge') list.forEach(function (handle) { if (next.indexOf(handle) < 0) next.push(handle); });
      return write(next);
    }
    return {
      read: read, write: write, toggle: toggle, importList: importList,
      remove: function (handle) { return write(read().filter(function (item) { return item !== handle; })); },
      isPersistent: function () { read(); return !volatile; },
      refresh: function () { volatile = false; return emit(null, 'external'); },
      storageKey: function () { return key; },
      currentScope: function () { return scope; },
      useScope: function (customerId) {
        if (customerId !== null && !/^[1-9][0-9]{0,19}$/.test(String(customerId))) return false;
        scope = customerId === null ? null : String(customerId);
        key = scope ? KEY + ':customer:' + scope : KEY;
        memory = []; volatile = false; locked = false; emit(null, 'scope'); return true;
      },
      lock: function (error) { locked = true; memory = []; emit(error || 'session', 'session'); },
      subscribe: function (subscriber) {
        subscribers.push(subscriber);
        return function () { subscribers = subscribers.filter(function (item) { return item !== subscriber; }); };
      }
    };
  }

  function install(win) {
    if (win.PGWishlist) return win.PGWishlist;
    var doc = win.document;
    var storage;
    try { storage = win.localStorage; } catch (e) { storage = null; }
    var store = createStore(storage, function (detail) { doc.dispatchEvent(new win.CustomEvent('pg:wish-updated', { detail: detail })); });
    // Choose the server-rendered identity before any badges or product lists paint.
    try {
      var syncNode = doc.querySelector && doc.querySelector('[data-pg-wishlist-sync-config]');
      var syncConfig = syncNode && JSON.parse(syncNode.textContent);
      if (syncConfig && syncConfig.enabled === true) {
        if (!store.useScope(syncConfig.customer_id === null ? null : String(syncConfig.customer_id))) store.lock();
      }
    } catch (_) { store.lock(); }
    win.PGWishlist = Object.assign({ KEY: KEY, LIMIT: LIMIT, validHandle: validHandle, validList: validList, clean: clean, parseStored: parseStored, encodeShare: encodeShare, decodeShare: decodeShare }, store);

    function syncStates(event) {
      var list = store.read();
      doc.querySelectorAll('[data-pg-wish-toggle]').forEach(function (button) {
        var handle = button.getAttribute('data-product-handle');
        var selected = list.indexOf(handle) >= 0;
        button.setAttribute('data-pg-in-wish', selected ? 'true' : 'false');
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      doc.querySelectorAll('[data-pg-wish-count]').forEach(function (element) {
        element.textContent = String(list.length);
        element.setAttribute('data-empty', list.length ? 'false' : 'true');
      });
      if (event && event.detail) {
        doc.querySelectorAll('[data-pg-wish-feedback]').forEach(function (element) {
          var pageOwnsFeedback = doc.querySelector && doc.querySelector('[data-pg-wishlist]');
          element.textContent = !pageOwnsFeedback && event.detail.error ? element.getAttribute('data-' + event.detail.error) || '' : '';
        });
      }
    }
    function wire() {
      doc.querySelectorAll('[data-pg-wish-toggle]').forEach(function (button) {
        if (button.dataset.pgWishWired === '1') return;
        button.dataset.pgWishWired = '1';
        button.addEventListener('click', function (event) {
          event.preventDefault();
          store.toggle(button.getAttribute('data-product-handle'));
        });
      });
      syncStates();
    }
    doc.addEventListener('pg:wish-updated', syncStates);
    doc.addEventListener('shopify:section:load', wire);
    if (win.addEventListener) {
      win.addEventListener('storage', function (event) { if (event.key === store.storageKey() || event.key === null) store.refresh(); });
      win.addEventListener('pageshow', function (event) { if (event.persisted) store.refresh(); });
    }
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', wire);
    else wire();
    doc.dispatchEvent(new win.CustomEvent('pg:wish-ready', { detail: {} }));
    return win.PGWishlist;
  }
  return { KEY: KEY, LIMIT: LIMIT, MAX_FRAGMENT: MAX_FRAGMENT, validHandle: validHandle, validList: validList, clean: clean, parseStored: parseStored, encodeShare: encodeShare, decodeShare: decodeShare, createStore: createStore, install: install };
});
